import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { jobsApi, type Job } from '../services/api';
import { formatDate, getStatusColor } from '../lib/utils';
import { Checkbox } from '../components/ui/checkbox';
import { Trash2 } from 'lucide-react';
import MarkdownRenderer from '../components/MarkdownRenderer';
import { useToast } from '../components/ui/use-toast';
import ConfirmDialog from '../components/ConfirmDialog';

const JobsPage: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [selectedJobIds, setSelectedJobIds] = useState<number[]>([]);
  const [readerOpen, setReaderOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [jobIdToDelete, setJobIdToDelete] = useState<number | null>(null);
  const detailsRef = useRef<HTMLDivElement | null>(null);

  const { toast } = useToast();

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      const jobList = await jobsApi.getJobs();
      setJobs(jobList);
    } catch (error) {
      console.error('Failed to load jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleJobSelection = (jobId: number) => {
    setSelectedJobIds((prev: number[]) =>
      prev.includes(jobId) ? prev.filter((id: number) => id !== jobId) : [...prev, jobId]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedJobIds.length === 0) return;
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    const idsToDelete = jobIdToDelete !== null ? [jobIdToDelete] : selectedJobIds;
    if (idsToDelete.length === 0) {
      setConfirmOpen(false);
      setJobIdToDelete(null);
      return;
    }

    try {
      await jobsApi.deleteJobs(idsToDelete);
      setJobs((prev: Job[]) => prev.filter((job: Job) => !idsToDelete.includes(job.id)));
      setSelectedJobIds((prev: number[]) => prev.filter((id: number) => !idsToDelete.includes(id)));
      if (selectedJob && idsToDelete.includes(selectedJob.id)) {
        setSelectedJob(null);
      }
      toast({ title: `Deleted ${idsToDelete.length} job(s)` });
    } catch (error) {
      console.error('Failed to delete jobs:', error);
      toast({ variant: 'destructive', title: 'Failed to delete jobs', description: 'Please try again.' });
    } finally {
      setConfirmOpen(false);
      setJobIdToDelete(null);
    }
  };

  const refreshJob = async (jobId: number) => {
    try {
      const updatedJob = await jobsApi.getJob(jobId);
      setJobs(jobs.map((job: Job) => job.id === jobId ? updatedJob : job));
      if (selectedJob && selectedJob.id === jobId) {
        setSelectedJob(updatedJob);
      }
    } catch (error) {
      console.error('Failed to refresh job:', error);
    }
  };

  const handleSelectJob = (job: Job) => {
    setSelectedJob(job);
    if (window.innerWidth < 1024) {
      setTimeout(() => {
        detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
    }
  };

  const handleCopyNotes = (notes: string) => {
    navigator.clipboard.writeText(notes || '');
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const downloadMd = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename && filename.endsWith('.md') ? filename : `${filename || 'notes'}.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading jobs...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6 px-4 sm:px-6 lg:px-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Job History</h1>
        <p className="text-muted-foreground mt-2">
          View and manage your note generation jobs
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 xl:gap-8">
        {/* Jobs List */}
        <div className="space-y-4 lg:col-span-4 xl:col-span-3 lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto px-4 lg:pr-1">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Recent Jobs</h2>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setMultiSelectMode((m: boolean) => { const next = !m; if (!next) setSelectedJobIds([]); return next; })}>
                {multiSelectMode ? 'Done' : 'Multiple Select'}
              </Button>
              {multiSelectMode && selectedJobIds.length > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDeleteSelected}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete ({selectedJobIds.length})
                </Button>
              )}
            </div>
          </div>
          {jobs.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center">
                <p className="text-muted-foreground">No jobs found. Upload a file to get started!</p>
              </CardContent>
            </Card>
          ) : (
            jobs.map((job: Job) => (
              <Card
                key={job.id}
                className={`relative cursor-pointer transition-colors mx-3 ${selectedJob?.id === job.id ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : 'hover:bg-accent'}`}
                onClick={() => handleSelectJob(job)}
              >
                {multiSelectMode && (
                  <div className="absolute top-3 left-4 z-10" onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
                    <Checkbox
                      id={`job-${job.id}`}
                      checked={selectedJobIds.includes(job.id)}
                      onCheckedChange={() => handleJobSelection(job.id)}
                      aria-label="Select job"
                    />
                  </div>
                )}
                <CardHeader className={`pb-3 ${multiSelectMode ? 'pl-12' : ''}`}>
                  <div className="flex items-center justify-between min-w-0">
                    <CardTitle className="text-lg truncate">
                      {job.meeting_title || job.original_filename}
                    </CardTitle>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                      {job.status.toUpperCase()}
                    </span>
                  </div>
                  <CardDescription>
                    Created {formatDate(job.created_at)}
                  </CardDescription>
                </CardHeader>
                <CardContent className={`pt-0 ${multiSelectMode ? 'pl-12' : ''}`}>
                  <div className="flex items-center justify-between min-w-0">
                    <span className="text-sm text-muted-foreground max-w-[65%] truncate">
                      {job.original_filename}
                    </span>
                    {(job.status === 'queued' || job.status === 'processing' || job.status === 'converting') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          refreshJob(job.id);
                        }}
                      >
                        Refresh
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Job Details */}
        <div className="lg:col-span-8 xl:col-span-9 lg:min-h-[60vh]" ref={detailsRef}>
          <h2 className="text-xl font-semibold mb-4">Job Details</h2>
          {selectedJob && (
            <Button className="mb-3 lg:hidden" variant="outline" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              Back to Jobs
            </Button>
          )}
          {selectedJob ? (
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="min-w-0">
                      <CardTitle className="truncate break-words">{selectedJob.meeting_title || selectedJob.original_filename}</CardTitle>
                      <div className="mt-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedJob.status)}`}>
                          {selectedJob.status.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      setJobIdToDelete(selectedJob.id);
                      setConfirmOpen(true);
                    }}
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium mb-2">File Information</h4>
                  <p className="text-sm text-muted-foreground">
                    Original filename: {selectedJob.original_filename}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Created: {formatDate(selectedJob.created_at)}
                  </p>
                </div>

                {selectedJob.meeting_date && (
                  <div>
                    <h4 className="font-medium mb-2">Meeting Date</h4>
                    <p className="text-sm">{selectedJob.meeting_date}</p>
                  </div>
                )}

                {selectedJob.attendees && selectedJob.attendees.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Attendees</h4>
                    <p className="text-sm">{selectedJob.attendees.join(', ')}</p>
                  </div>
                )}

                {selectedJob.context && (
                  <div>
                    <h4 className="font-medium mb-2">Context</h4>
                    <p className="text-sm">{selectedJob.context}</p>
                  </div>
                )}

                {selectedJob.error_message && (
                  <div>
                    <h4 className="font-medium mb-2 text-destructive">Error</h4>
                    <p className="text-sm text-destructive">{selectedJob.error_message}</p>
                  </div>
                )}

                {selectedJob.generated_notes && (
                  <div className="bg-muted rounded-md">
                    <div className="flex items-center justify-between p-3 border-b">
                      <h4 className="font-medium">Generated Notes</h4>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCopyNotes(selectedJob.generated_notes || '')}
                        >
                          {copied ? 'Copied' : 'Copy'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            downloadMd(
                              selectedJob.generated_notes || '',
                              selectedJob.meeting_title || selectedJob.original_filename || 'notes'
                            )
                          }
                        >
                          Download .md
                        </Button>
                        <Button size="sm" onClick={() => setReaderOpen(true)}>Reader Mode</Button>
                      </div>
                    </div>
                    <div className="p-4 lg:max-h-[calc(100vh-18rem)] lg:overflow-y-auto">
                      <MarkdownRenderer content={selectedJob.generated_notes} />
                    </div>
                  </div>
                )}

                {(selectedJob.status === 'queued' || selectedJob.status === 'processing' || selectedJob.status === 'converting') && (
                  <Button
                    variant="outline"
                    onClick={() => refreshJob(selectedJob.id)}
                    className="w-full"
                  >
                    Refresh Status
                  </Button>
                )}

                {readerOpen && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-background w-[min(1100px,95vw)] h-[min(85vh,95vh)] rounded-lg shadow-lg overflow-hidden flex flex-col">
                      <div className="p-3 border-b flex items-center justify-between">
                        <div className="font-medium">Reader Mode</div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              downloadMd(
                                selectedJob?.generated_notes || '',
                                selectedJob?.meeting_title || selectedJob?.original_filename || 'notes'
                              )
                            }
                          >
                            Download .md
                          </Button>
                          <Button size="sm" onClick={() => setReaderOpen(false)}>Close</Button>
                        </div>
                      </div>
                      <div className="p-6 overflow-y-auto">
                        <div className="mx-auto max-w-prose">
                          <MarkdownRenderer content={selectedJob?.generated_notes || ''} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-6 text-center">
                <p className="text-muted-foreground">Select a job to view details</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(open: boolean) => {
          setConfirmOpen(open);
          if (!open) setJobIdToDelete(null);
        }}
        title={jobIdToDelete !== null ? 'Delete this job?' : `Delete ${selectedJobIds.length} job(s)?`}
        description="This action cannot be undone."
        confirmText="Delete"
        variant="destructive"
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default JobsPage;
